/**
 * Equipment level and the killed share of the mounted part of a unit (R1-09, 2026-10-25, Dr. Tatar: wire what is sourced).
 *
 * WHAT IT DOES. The equipment level (weapons, vehicles, optics, communications; the soldier's body armor is the separate body armor setting)
 * already moves the NUMBER of casualties (EQUIPMENT_LEVELS.casualty_multiplier). This adds the effect that the public record supports: vehicle
 * protection changes how many of the casualties of the MOUNTED troops are killed. The equipment kia_multiplier acts on the killed share of the
 * mounted part of each unit only; the total number of casualties is unchanged (killed become wounded, as with body armor):
 *   killed-share factor of a unit = 1 - mounted_share x (1 - kia_multiplier)
 *   killed-share factor of the brigade = casualty-weighted mean over its units.
 * Dismounted infantry (INFANTRY, AIRBORNE, AIR_ASSAULT, MOUNTAIN) have no row: no effect.
 *
 * SOURCES (docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 5): DoD officials: casualty rate MRAP 6, Abrams 15, up-armored HMMWV 22 percent
 * (CRS RS22707); Deputy Secretary of Defense Carter: 14 times more likely to survive in an MRAP; DoD Inspector General figure quoted by
 * Gayl: 1 killed in MRAPs per 4 to 5 in armored HMMWVs (Iraq, IED attacks). CONTESTED by Sullivan and Rolfe (no large effect beyond factory-armored
 * HMMWVs; the fall in casualties follows the fall in fighting intensity). The kia_multiplier values of EQUIPMENT_LEVELS (0.50 to 1.60) are
 * conservative against the official figures (0.27 casualty-rate ratio, 0.2 to 0.25 killed ratio, best case) and EDUCATED GUESS as levels.
 * NOT WIRED, on purpose: dow_multiplier (no study ties general equipment to died of wounds; the wound care chain owns it).
 * The mounted shares below are EDUCATED GUESS: the share of the casualty exposure of a unit that happens inside a vehicle (a tank crew, a
 * mechanized infantry squad before dismounting, a convoy).
 */
export const EQUIPMENT_KILL_EFFECT_ON = true;   // switched on 2026-10-25 (Dr. Tatar approved after the before and after numbers, docs/TODO_CURRENT.md R1-09)

export const EQUIPMENT_KILL_EFFECT_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Direction and size for the best protected vehicles: DoD figures (contested); the equipment level multipliers and the mounted shares are guesses'
});

/** Share of the casualty exposure of the unit type that happens inside a vehicle. Types not listed are dismounted or not vehicle-borne: 0. */
export const MOUNTED_SHARE_BY_UNIT_TYPE = Object.freeze({
  ARMOR:           0.7,
  MECHANIZED:      0.5,
  STRYKER:         0.5,
  RECONNAISSANCE:  0.6,
  ARTILLERY:       0.4,
  AIR_DEFENSE:     0.5,
  ENGINEERS:       0.3,
  MILITARY_POLICE: 0.5,
  LOGISTICS:       0.3,   // soft-skinned convoys, escorts: less of an effect of the equipment level than for armored vehicles
  TRANSPORT:       0.4,
  SUPPLY:          0.2,
  MAINTENANCE:     0.2,
  MEDICAL:         0.3,
  MEDICAL_SUPPORT: 0.3,
  HQ:              0.2,
  SIGNAL:          0.2,
  SPECIAL_FORCES:  0.1
});

/** Killed-share factor of one unit type for an equipment kia_multiplier (1 = no effect). */
export function killedShareFactorOfUnit(unitType, kiaMultiplier) {
  if (!Number.isFinite(kiaMultiplier) || kiaMultiplier <= 0) {
    throw new Error(`killedShareFactorOfUnit: kia_multiplier must be a positive number (got: ${kiaMultiplier})`);
  }
  const m = Object.prototype.hasOwnProperty.call(MOUNTED_SHARE_BY_UNIT_TYPE, unitType) ? MOUNTED_SHARE_BY_UNIT_TYPE[unitType] : 0;
  return 1 - m * (1 - kiaMultiplier);
}

/**
 * Killed-share factor of a brigade: the casualty-weighted mean over its sub-units.
 * @param {Array<{unit_type:string, casualties:number}>} subUnitBreakdown from calculateSubUnitCasualties
 * @param {number} kiaMultiplier EQUIPMENT_LEVELS[level].kia_multiplier
 * @returns {number} factor to put on the killed share (1 when the switch is off, there are no casualties, or nothing is vehicle-borne)
 */
export function killedShareFactor(subUnitBreakdown, kiaMultiplier, on = EQUIPMENT_KILL_EFFECT_ON) {
  if (!on || !Array.isArray(subUnitBreakdown)) return 1;
  const total = subUnitBreakdown.reduce((s, su) => s + Math.max(0, su.casualties || 0), 0);
  if (total <= 0) return 1;
  return subUnitBreakdown.reduce((s, su) => s + (Math.max(0, su.casualties || 0) / total) * killedShareFactorOfUnit(su.unit_type, kiaMultiplier), 0);
}
